/**
 * REWE Digital GmbH.
 * IT-Verticals
 *
 * zr-search-form component
 */

@use "../zr-theme/zr-variables.scss" as *;
@use "../zr-css-icon/zr-css-icon.scss" as i;
@use "../zr-theme/functions/fonts" as f;
@use "../zr-theme/fonts/open-sans.scss";

// component name
$component: 'zr-search-form';

// component vars defaults
:host {
    --focus-color: var(--ci-primary600);
    box-sizing: border-box;
    display: block;
    padding: 12px;
    position: relative;
    width: 100%;
    align-self: center;
}

.#{$component} {
    display: flex;
    justify-content: center;
}

.#{$component}__formular {
    display: flex;
    width: 100%;
    border-radius: 4px;
    border: 2px solid white;

    &.input-focus {
        border-color: var(--focus-color, var(--ci-primary600));
    }

    &.flyout-visible {
        border-radius: 4px 4px 0 0;
    }
}


.#{$component}__input {
    border: 2px solid white;
    color: var(--ci-neutral900, #3c3c3c);
    font-size: f.calc-em(14);
    line-height: f.calc-em(20);
    padding: 3px 3px 3px 8px;
    width: 100%;
}



.#{$component}__btn-submit {
    background-color: var(--ci-neutral100, #fff);
    border: none;
    border-left: 1px solid var(--ci-neutral400, #d1d1d1);
    font-size: 0;
    padding: 4px 10px;

    @include i.get-icon-left(lens);

    &::before {
        height: 18px;
        width: 18px;
    }

    &:active,
    &:hover {
        &::before {
            transform: scale(1.2);
        }
    }

    &:focus {
        background-color: var(--focus-color, var(--ci-primary600));

        &::before {
            background-color: var(--ci-neutral100, #fff);
        }
    }
}


.#{$component}__btn-reset {
    font-size: 0;
    border: none;
    border-left: 1px solid var(--ci-neutral400, #d1d1d1);
    background-color: var(--ci-neutral100, #fff);
    padding: 4px 10px;

    @include i.get-icon-left(close);

    &::before {
        transition: transform 150ms ease-in;
        height: 14px;
        width: 14px;
    }

    &:active,
    &:hover {
        &::before {
            transform: rotate(90deg);
        }
    }

    &:focus {
        background-color: var(--focus-color, var(--ci-primary600));

        &::before {
            background-color: var(--ci-neutral100, #fff);
        }
    }
}

.#{$component}__flyout {
    -webkit-font-smoothing: antialiased;
    background-color: var(--vx-search-flyout-background-color, var(--ci-neutral100, #fff));
    border-top: 1px solid var(--ci-neutral400, #d1d1d1);
    box-shadow: var(--vx-search-flyout-box-shadow, 0 3px 3px 0 rgb(0 0 0 / 50%));
    border-radius: 0 0 4px 4px;
    color: var(--vx-search-flyout-text-color, var(--ci-neutral900, #000));
    padding: 8px 16px;
    position: absolute;
    top: calc(100% - 12px);
    width: auto;
    left: 12px;
    right: 12px;
    opacity: 0;
    z-index: 2;
    pointer-events: none;
    transform: translateY(-12px);
    transition: none;

    .flyout-visible & {
        min-height: 80px;
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
        transition: opacity 0.3s ease-in, transform 0.3s ease-in;
    }
}
.#{$component}__list-header {
    color: var(--ci-neutral600, #878787);
    line-height: 16px;
    font-size: 12px;
    margin-top: 0;
}

.#{$component}__suggestions {
    list-style: none;
    padding: 0;
    margin-bottom: 16px;
}

.#{$component}__suggestions-logo {
    max-height: 40px;
    padding-right: 16px;
}

.#{$component}__suggestions-banner {
    max-width: 100%;
}

.#{$component}__products{
    list-style: none;
    padding: 0;
}

.#{$component}__suggestion-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    line-height: 24px;
    padding: 8px 0;
    cursor: pointer;
    margin-bottom: 8px;
    color: var(--ci-neutral700, #5b5b5b);


    a:first-child {
        flex: 1;
        display: flex;
        align-items: center;
        text-decoration: none;
        color: var(--ci-neutral800);

        @include i.get-icon-left(lens);

        &::before {
            background-color: var(--ci-neutral700);
            height: 17px;
            width: 17px;
            margin: 0 5px;
        }

        &:focus,
        &:active,
        &:hover {
            color: var(--ci-primary500);
        }
    }

    a:focus-visible {
        outline: 2px solid var(--focus-color, var(--ci-primary600)) !important;
    }
}

.#{$component}__product-item {
    margin-bottom: 12px;
}

.#{$component}__no-results {
    padding-top: 20px;
}

.#{$component}__results-button-container {
    display: flex;
    justify-content: center;
    padding: 0 0 8px;

    .#{$component}__results-button {
        width: 100%;
        font-size: f.calc-em(14);
        background: none;
        color: var(--ci-neutral900);
        border: 0.3px solid var(--ci-neutral300);
        border-radius: 4px;

        &:focus,
        &:active,
        &:hover {
            color: var(--ci-primary500);
            box-shadow: 0 2px 6px -1px var(--ci-neutral400);
            border: none;
        }

        &:focus-visible {
            outline: 2px solid var(--focus-color, var(--ci-primary600)) !important;
        }
    }
}

// media query: tablet and above
@media only screen and (min-width: $tablet-screen) {
    .#{$component}__flyout {
        z-index: 1040;

        .#{$component}__results-button {
            font-size: f.calc-em(16);
            padding: 8px 16px;
        }
    }
}

